import * as React from 'react';
import PersistentStoreService from '~services/PersistentStoreService';

export interface ColumnDefinition {
  id: string;
  label: string;
}

export interface ColumnState {
  id: string;
  visible: boolean;
}

const storageKeyFor = (tableId: string) => `deviceTables.columns.${tableId}`;

const hydrateTable = (
  allColumns: ColumnDefinition[],
  defaultColumns: ColumnDefinition[],
  saved: ColumnState[] | undefined
): ColumnState[] => {
  const allIds = new Set(allColumns.map((d) => d.id));
  const defaultIds = new Set(defaultColumns.map((d) => d.id));
  const merged: ColumnState[] = [];
  const seen = new Set<string>();
  if (saved) {
    for (const s of saved) {
      if (!allIds.has(s.id) || seen.has(s.id)) continue;
      merged.push({ id: s.id, visible: !!s.visible });
      seen.add(s.id);
    }
  }
  for (const d of allColumns) {
    if (!seen.has(d.id)) merged.push({ id: d.id, visible: defaultIds.has(d.id) });
  }
  return merged;
};

interface Props {
  tableId: string | undefined;
  allColumns: ColumnDefinition[];
  defaultColumns: ColumnDefinition[];
}

export function useColumnManager({ tableId, allColumns, defaultColumns }: Props) {
  const storageKey = tableId ? storageKeyFor(tableId) : undefined;

  const initialColumns = React.useMemo(() => {
    const saved = storageKey ? PersistentStoreService.get<ColumnState[]>(storageKey) : undefined;
    return hydrateTable(allColumns, defaultColumns, saved);
  }, [storageKey, allColumns, defaultColumns]);

  const [columns, setColumnsState] = React.useState<ColumnState[]>(initialColumns);

  const [trackedStorageKey, setTrackedStorageKey] = React.useState(storageKey);
  if (storageKey !== trackedStorageKey) {
    setTrackedStorageKey(storageKey);
    setColumnsState(initialColumns);
  }

  const persist = React.useCallback(
    (next: ColumnState[]) => {
      if (storageKey) PersistentStoreService.set(storageKey, next);
    },
    [storageKey]
  );

  const setColumns = React.useCallback(
    (next: ColumnState[]) => {
      setColumnsState(next);
      persist(next);
    },
    [persist]
  );

  const toggleColumn = React.useCallback(
    (id: string) =>
      setColumnsState((prev) => {
        const next = prev.map((c) => (c.id === id ? { ...c, visible: !c.visible } : c));
        persist(next);
        return next;
      }),
    [persist]
  );

  const hideColumn = React.useCallback(
    (id: string) =>
      setColumnsState((prev) => {
        const next = prev.map((c) => (c.id === id ? { ...c, visible: false } : c));
        persist(next);
        return next;
      }),
    [persist]
  );

  const showColumn = React.useCallback(
    (id: string) =>
      setColumnsState((prev) => {
        const next = prev.map((c) => (c.id === id ? { ...c, visible: true } : c));
        persist(next);
        return next;
      }),
    [persist]
  );

  const setAll = React.useCallback(
    (visible: boolean) =>
      setColumnsState((prev) => {
        const next = prev.map((c) => ({ ...c, visible }));
        persist(next);
        return next;
      }),
    [persist]
  );

  const resetColumns = React.useCallback(() => {
    const defaultIds = new Set(defaultColumns.map((d) => d.id));
    const next = allColumns.map((d) => ({
      id: d.id,
      visible: defaultIds.has(d.id),
    }));
    setColumnsState(next);
    persist(next);
  }, [allColumns, defaultColumns, persist]);

  const isDirty = React.useMemo(() => {
    if (columns.length !== allColumns.length) return true;
    const defaultIds = new Set(defaultColumns.map((d) => d.id));
    return columns.some((c, i) => {
      const d = allColumns[i];
      return !d || d.id !== c.id || c.visible !== defaultIds.has(c.id);
    });
  }, [columns, allColumns, defaultColumns]);

  return {
    columns,
    setColumns,
    toggleColumn,
    hideColumn,
    showColumn,
    setAll,
    resetColumns,
    isDirty,
  };
}
